import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { cn } from "@/cn";
import { listPrompts, type PromptTemplate } from "@/api/prompts";
import { useClientPagination } from "@/hooks/usePagination";
import { useCursorPagination } from "@/hooks/useCursorPagination";
import { useSyncCursorPage } from "@/hooks/useSyncCursorPage";
import { PaginationBar } from "@/components/pagination-bar";
import {
  CollectionCommandBar,
  DataTable,
  EntityCard,
  EntityCompactItem,
  EntityListLayout,
  useClientTableSort,
  useListViewMode,
  type Column,
  type EntityFilterSelectField,
  type EntitySortOption,
} from "@/components/collection";
import {
  categoryColor,
  categoryLabel,
  filterPrompts,
  promptCategoryTabs,
  promptRequirementSummary,
  type PromptCategoryFilter,
} from "@/lib/prompt-ui";

const ALLOWED_MODES = ["cards", "list", "table"] as const;

const SORT_OPTIONS: EntitySortOption[] = [
  { field: "name", order: "asc", label: "Name A–Z" },
  { field: "category", order: "asc", label: "Category" },
  { field: "kind", order: "asc", label: "Kind" },
];

function applySort(
  items: PromptTemplate[],
  sort: EntitySortOption,
): PromptTemplate[] {
  const dir = sort.order === "asc" ? 1 : -1;
  return [...items].sort((a, b) => {
    if (sort.field === "category") {
      return (a.category ?? "").localeCompare(b.category ?? "") * dir;
    }
    if (sort.field === "kind") {
      return a.kind.localeCompare(b.kind) * dir;
    }
    return a.name.localeCompare(b.name) * dir;
  });
}

export function SettingsPromptsPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] =
    useState<PromptCategoryFilter>("all");
  const [sortOption, setSortOption] = useState<EntitySortOption>(SORT_OPTIONS[0]);

  const { viewMode, setViewMode, allowedModes } = useListViewMode({
    storageKey: "viewMode_settings_prompts",
    defaultMode: "cards",
    allowedModes: [...ALLOWED_MODES],
  });

  const useServerCursor = !searchQuery.trim();

  const cursorPag = useCursorPagination([categoryFilter]);

  const categoryParam =
    categoryFilter === "all" ? undefined : categoryFilter;

  const {
    data: cursorData,
    isLoading: cursorLoading,
    error: cursorError,
  } = useQuery({
    queryKey: [
      "prompts",
      "cursor",
      categoryParam,
      cursorPag.pageSize,
      cursorPag.cursor,
    ],
    queryFn: () =>
      listPrompts({
        category: categoryParam,
        limit: cursorPag.pageSize,
        cursor: cursorPag.cursor,
      }),
    enabled: useServerCursor,
    staleTime: 30_000,
  });

  useSyncCursorPage(
    cursorPag.setNextCursor,
    cursorData,
    useServerCursor && !cursorLoading && !!cursorData,
  );

  const {
    data: allData,
    isLoading: allLoading,
    error: allError,
  } = useQuery({
    queryKey: ["prompts", "all", categoryParam],
    queryFn: () => listPrompts({ category: categoryParam }),
    enabled: !useServerCursor,
    staleTime: 30_000,
  });

  const { data: tabsPrompts = [] } = useQuery({
    queryKey: ["prompts", "all-for-tabs"],
    queryFn: async () => (await listPrompts()).prompts,
    staleTime: 60_000,
  });

  const categoryTabs = promptCategoryTabs(tabsPrompts);

  const isLoading = useServerCursor ? cursorLoading : allLoading;
  const error = useServerCursor ? cursorError : allError;

  const pagePrompts = useMemo(() => {
    if (useServerCursor) {
      return applySort(cursorData?.prompts ?? [], sortOption);
    }
    return applySort(
      filterPrompts(allData?.prompts ?? [], {
        search: searchQuery,
        category: categoryFilter,
      }),
      sortOption,
    );
  }, [
    useServerCursor,
    cursorData?.prompts,
    allData?.prompts,
    searchQuery,
    categoryFilter,
    sortOption,
  ]);

  const clientPag = useClientPagination(
    useServerCursor ? [] : pagePrompts,
    [searchQuery, categoryFilter, sortOption.field],
  );

  const pageItems = useServerCursor ? pagePrompts : clientPag.pageItems;
  const totalForEmpty = useServerCursor
    ? (cursorData?.prompts.length ?? 0)
    : (allData?.prompts.length ?? 0);

  const getSortValue = useCallback((p: PromptTemplate, key: string) => {
    if (key === "category") return p.category ?? "";
    if (key === "kind") return p.kind;
    if (key === "version") return p.version;
    return p.name;
  }, []);

  const { sortedItems, sortKey, sortDirection, onSortChange } =
    useClientTableSort({
      items: pageItems.map((p) => ({ ...p, id: p.kind })),
      defaultKey: null,
      getValue: getSortValue,
    });

  const filterFields: EntityFilterSelectField[] =
    categoryTabs.length > 1
      ? [
          {
            id: "category",
            label: "Category",
            value: categoryFilter === "all" ? "" : categoryFilter,
            allLabel: "All categories",
            options: categoryTabs
              .filter((t) => t !== "all")
              .map((t) => ({
                value: t,
                label: categoryLabel(t),
              })),
            onChange: (v) =>
              setCategoryFilter((v || "all") as PromptCategoryFilter),
          },
        ]
      : [];

  const columns: Column<PromptTemplate & { id: string }>[] = [
    {
      key: "name",
      header: "Name",
      sortable: true,
      render: (p) => (
        <span className="font-medium text-foreground">{p.name}</span>
      ),
    },
    {
      key: "category",
      header: "Category",
      sortable: true,
      render: (p) =>
        p.category ? (
          <span
            className={cn(
              "rounded-full px-2 py-0.5 text-[10px] font-medium",
              categoryColor(p.category),
            )}
          >
            {categoryLabel(p.category)}
          </span>
        ) : (
          "—"
        ),
    },
    {
      key: "kind",
      header: "Kind",
      sortable: true,
      render: (p) => (
        <span className="text-muted-foreground">{p.kind}</span>
      ),
    },
    {
      key: "version",
      header: "Version",
      sortable: true,
      render: (p) => <span className="text-muted-foreground">v{p.version}</span>,
    },
  ];

  const showCommandBar =
    !isLoading && !error && (useServerCursor || totalForEmpty > 0);
  const hasItems = pageItems.length > 0;
  const showEmptyCatalog = !isLoading && !error && totalForEmpty === 0;
  const showNoMatch =
    !isLoading &&
    !error &&
    !useServerCursor &&
    totalForEmpty > 0 &&
    pagePrompts.length === 0;

  return (
    <div className="space-y-6">
      <header className="mb-8">
        <h1 className="text-2xl font-bold text-foreground">Prompt Templates</h1>
        <p className="text-muted-foreground">
          System prompt templates used by the AI assistant for different
          workflows.
        </p>
      </header>

      {showCommandBar && (
        <div>
          <CollectionCommandBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder="Search by name, kind, category, or description…"
            sortOption={sortOption}
            onSortChange={setSortOption}
            sortOptions={SORT_OPTIONS}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            allowedModes={allowedModes}
            filterFields={filterFields}
            activeFilterCount={categoryFilter !== "all" ? 1 : 0}
            onClear={() => {
              setSearchQuery("");
              setCategoryFilter("all");
            }}
          />
          {!useServerCursor && (
            <p className="mt-2 text-xs text-muted-foreground">
              {pagePrompts.length === 0
                ? `0 of ${totalForEmpty}`
                : `Showing ${clientPag.showingFrom}–${clientPag.showingTo} of ${pagePrompts.length}`}
            </p>
          )}
        </div>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
        </div>
      )}
      {error && (
        <p className="text-sm text-destructive">Failed to load prompt templates.</p>
      )}

      {showEmptyCatalog && (
        <div className="rounded-lg border border-dashed border-border p-8 text-center">
          <p className="text-sm text-muted-foreground">No prompt templates found.</p>
        </div>
      )}

      {showNoMatch && (
        <div className="rounded-lg border border-border bg-card p-8 text-center">
          <p className="text-sm text-muted-foreground">
            No prompts match your filters.
          </p>
        </div>
      )}

      {!isLoading && !error && hasItems && (
        <>
          <EntityListLayout
            viewMode={viewMode}
            cards={
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {pageItems.map((prompt) => (
                  <EntityCard
                    key={prompt.kind}
                    id={prompt.kind}
                    title={prompt.name}
                    description={prompt.description || prompt.kind}
                    primaryBadge={
                      prompt.category
                        ? {
                            label: categoryLabel(prompt.category),
                            colorClass: categoryColor(prompt.category),
                          }
                        : undefined
                    }
                    metadata={[
                      { value: prompt.kind },
                      { value: `v${prompt.version}` },
                      { value: promptRequirementSummary(prompt) },
                    ]}
                    onClick={() =>
                      navigate(`/settings/prompts/${prompt.kind}`)
                    }
                  />
                ))}
              </div>
            }
            list={
              <div className="space-y-2">
                {pageItems.map((prompt) => (
                  <EntityCompactItem
                    key={prompt.kind}
                    id={prompt.kind}
                    title={prompt.name}
                    description={prompt.description || prompt.kind}
                    badges={
                      prompt.category
                        ? [
                            {
                              label: categoryLabel(prompt.category),
                              colorClass: categoryColor(prompt.category),
                            },
                          ]
                        : undefined
                    }
                    metadata={[
                      { value: prompt.kind },
                      { value: `v${prompt.version}` },
                    ]}
                    onClick={() =>
                      navigate(`/settings/prompts/${prompt.kind}`)
                    }
                  />
                ))}
              </div>
            }
            table={
              <DataTable
                data={sortedItems}
                columns={columns}
                onRowClick={(p) => navigate(`/settings/prompts/${p.kind}`)}
                sortKey={sortKey}
                sortDirection={sortDirection}
                onSortChange={onSortChange}
              />
            }
          />
          {useServerCursor ? (
            <PaginationBar
              mode="cursor"
              page={cursorPag.page}
              pageSize={cursorPag.pageSize}
              itemCount={pageItems.length}
              showingFrom={
                pageItems.length === 0 ? 0 : cursorPag.showingFrom
              }
              showingTo={cursorPag.showingTo(pageItems.length)}
              hasMore={cursorPag.hasMore}
              canPrev={cursorPag.canPrev}
              onFirst={cursorPag.goFirst}
              onPrev={cursorPag.goPrev}
              onNext={cursorPag.goNext}
              onPageSizeChange={cursorPag.setPageSize}
            />
          ) : (
            <PaginationBar
              page={clientPag.page}
              pageSize={clientPag.pageSize}
              totalItems={pagePrompts.length}
              totalPages={clientPag.totalPages}
              showingFrom={clientPag.showingFrom}
              showingTo={clientPag.showingTo}
              onPageChange={clientPag.setPage}
              onPageSizeChange={clientPag.setPageSize}
            />
          )}
        </>
      )}
    </div>
  );
}
